// =============================================================
// API Client — Phase 3
// Centralized HTTP client for all backend requests.
// Injects JWT token from localStorage automatically.
// =============================================================

const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1';
export interface AIAssistantSource { type: string; label: string; value?: string; freshness?: string }
export interface AIAssistantAction { label: string; route: string }
export interface AIAssistantMessage { id: string; role: 'USER' | 'ASSISTANT'; content: string; sourcesJson?: AIAssistantSource[]; actionsJson?: AIAssistantAction[]; createdAt: string }
export interface AIAssistantConversation { id: string; title: string; createdAt: string; updatedAt: string; messages?: AIAssistantMessage[]; _count?: { messages: number } }

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  token?: string;
  timeoutMs?: number;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, token, timeoutMs } = options;

  const storedToken = token ?? localStorage.getItem('auth_token');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (storedToken) {
    headers['Authorization'] = `Bearer ${storedToken}`;
  }

  const signal = timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined;

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err: any) {
    if (
      err?.name === 'TimeoutError' ||
      err?.name === 'AbortError' ||
      err?.message?.includes('timeout') ||
      err?.message?.includes('aborted')
    ) {
      const isAi = endpoint.includes('/intelligence/') || Boolean(timeoutMs);
      throw new ApiError(
        504,
        'AI_TIMEOUT',
        isAi ? 'AI analysis timed out. Please try again.' : 'Request timed out. Please try again.'
      );
    }
    throw err;
  }

  const data = await response.json();

  if (!data.success) {
    throw new ApiError(
      response.status,
      data.error?.code ?? 'UNKNOWN',
      data.error?.message ?? 'An error occurred'
    );
  }

  return data.data as T;
}

export const aiAssistantApi = {
  list: (businessId: number | string) => request<AIAssistantConversation[]>(`/businesses/${businessId}/ai-assistant/conversations`),
  get: (businessId: number | string, conversationId: string) => request<AIAssistantConversation>(`/businesses/${businessId}/ai-assistant/conversations/${conversationId}`),
  remove: (businessId: number | string, conversationId: string) => request<{ deleted: boolean }>(`/businesses/${businessId}/ai-assistant/conversations/${conversationId}`, { method: 'DELETE' }),
  chat: (businessId: number | string, message: string, conversationId?: string) => request<{ conversationId: string; message: AIAssistantMessage; sources: AIAssistantSource[]; suggestedActions: AIAssistantAction[] }>(`/businesses/${businessId}/ai-assistant/chat`, { method: 'POST', body: { message, conversationId }, timeoutMs: 130000 }),
};

// -------------------------
// Auth
// -------------------------
export const authApi = {
  register: (body: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
  }) => request<{ token: string; user: AuthUser; organization: OrgSummary }>('/auth/register', { method: 'POST', body }),

  login: (body: { email: string; password: string }) =>
    request<{ token: string; user: AuthUser; organization: OrgSummary }>('/auth/login', { method: 'POST', body }),

  me: () => request<MeResponse>('/auth/me'),
};

// -------------------------
// Businesses & Related Sub-Resources
// -------------------------
export const businessApi = {
  list: () => request<Business[]>('/businesses'),
  get: (id: number | string) => request<Business>(`/businesses/${id}`),
  create: (body: CreateBusinessBody) =>
    request<Business>('/businesses', { method: 'POST', body }),
  update: (id: number | string, body: Partial<CreateBusinessBody>) =>
    request<Business>(`/businesses/${id}`, { method: 'PATCH', body }),
  delete: (id: number | string) => request<void>(`/businesses/${id}`, { method: 'DELETE' }),

  // Locations
  listLocations: (businessId: number | string) =>
    request<BusinessLocation[]>(`/businesses/${businessId}/locations`),
  createLocation: (businessId: number | string, body: Partial<BusinessLocation>) =>
    request<BusinessLocation>(`/businesses/${businessId}/locations`, { method: 'POST', body }),
  updateLocation: (businessId: number | string, locationId: number | string, body: Partial<BusinessLocation>) =>
    request<BusinessLocation>(`/businesses/${businessId}/locations/${locationId}`, { method: 'PATCH', body }),
  deleteLocation: (businessId: number | string, locationId: number | string) =>
    request<void>(`/businesses/${businessId}/locations/${locationId}`, { method: 'DELETE' }),

  // Services
  listServices: (businessId: number | string) =>
    request<BusinessService[]>(`/businesses/${businessId}/services`),
  createService: (businessId: number | string, body: { name: string; description?: string }) =>
    request<BusinessService>(`/businesses/${businessId}/services`, { method: 'POST', body }),
  updateService: (businessId: number | string, serviceId: number | string, body: { name?: string; description?: string }) =>
    request<BusinessService>(`/businesses/${businessId}/services/${serviceId}`, { method: 'PATCH', body }),
  deleteService: (businessId: number | string, serviceId: number | string) =>
    request<void>(`/businesses/${businessId}/services/${serviceId}`, { method: 'DELETE' }),

  // Target Locations
  listTargetLocations: (businessId: number | string) =>
    request<BusinessTargetLocation[]>(`/businesses/${businessId}/target-locations`),
  createTargetLocation: (businessId: number | string, body: { name: string; city?: string; state?: string; country?: string }) =>
    request<BusinessTargetLocation>(`/businesses/${businessId}/target-locations`, { method: 'POST', body }),
  updateTargetLocation: (businessId: number | string, locationId: number | string, body: { name?: string; city?: string; state?: string; country?: string }) =>
    request<BusinessTargetLocation>(`/businesses/${businessId}/target-locations/${locationId}`, { method: 'PATCH', body }),
  deleteTargetLocation: (businessId: number | string, locationId: number | string) =>
    request<void>(`/businesses/${businessId}/target-locations/${locationId}`, { method: 'DELETE' }),

  // Onboarding
  getOnboarding: (businessId: number | string) =>
    request<BusinessOnboarding>(`/businesses/${businessId}/onboarding`),
  updateOnboarding: (businessId: number | string, body: { currentStep?: number; completed?: boolean }) =>
    request<BusinessOnboarding>(`/businesses/${businessId}/onboarding`, { method: 'PATCH', body }),

  // Settings
  getSettings: (businessId: number | string) =>
    request<BusinessSettings>(`/businesses/${businessId}/settings`),
  updateSettings: (businessId: number | string, body: { primaryIndustry?: string; primaryCategory?: string; secondaryCategories?: string[]; targetAudience?: string; targetMarket?: string }) =>
    request<BusinessSettings>(`/businesses/${businessId}/settings`, { method: 'PATCH', body }),

  // SEO Project
  getSEOProject: (businessId: number | string) =>
    request<SEOProject>(`/businesses/${businessId}/seo-project`),

  // Website Audits (Phase 3)
  startAudit: (businessId: number | string, websiteUrl?: string) =>
    request<WebsiteAudit>(`/businesses/${businessId}/website-audits`, {
      method: 'POST',
      body: { websiteUrl },
    }),
  listAudits: (
    businessId: number | string,
    params?: { startDate?: string; endDate?: string; limit?: number }
  ) => {
    const q = new URLSearchParams();
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    if (params?.limit) q.append('limit', String(params.limit));
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<WebsiteAudit[]>(`/businesses/${businessId}/website-audits${qs}`);
  },
  getAudit: (businessId: number | string, auditId: number | string) =>
    request<WebsiteAudit>(`/businesses/${businessId}/website-audits/${auditId}`),
  getAuditPages: (businessId: number | string, auditId: number | string) =>
    request<WebsitePage[]>(`/businesses/${businessId}/website-audits/${auditId}/pages`),
  getAuditFindings: (
    businessId: number | string,
    auditId: number | string,
    params?: { category?: string; severity?: string }
  ) => {
    const q = new URLSearchParams();
    if (params?.category) q.append('category', params.category);
    if (params?.severity) q.append('severity', params.severity);
    const queryString = q.toString() ? `?${q.toString()}` : '';
    return request<SEOFinding[]>(`/businesses/${businessId}/website-audits/${auditId}/findings${queryString}`);
  },
  cancelAudit: (businessId: number | string, auditId: number | string) =>
    request<{ message: string; activeWasRunning: boolean }>(
      `/businesses/${businessId}/website-audits/${auditId}/cancel`,
      { method: 'POST' }
    ),
};

// -------------------------
// Google Integrations (Phase 4)
// -------------------------
export const googleApi = {
  getStatus: (businessId?: number | string) => 
    request<GoogleStatusResponse>(businessId ? `/integrations/google/status?businessId=${businessId}` : '/integrations/google/status'),
  getConnectUrl: (businessId?: number | string) =>
    request<{ authUrl: string; state: string }>(
      businessId ? `/integrations/google/connect?businessId=${businessId}` : '/integrations/google/connect'
    ),
  disconnect: (businessId?: number | string) => 
    request<{ message: string }>(
      businessId ? `/integrations/google/disconnect?businessId=${businessId}` : '/integrations/google/disconnect', 
      { method: 'POST' }
    ),

  // Search Console
  getSearchConsoleProperties: (businessId: number | string) =>
    request<{ connectedProperty: SearchConsolePropertyDto | null; availableProperties: SearchConsolePropertyOption[] }>(
      `/businesses/${businessId}/search-console/properties`
    ),
  selectSearchConsoleProperty: (businessId: number | string, siteUrl: string) =>
    request<SearchConsolePropertyDto>(`/businesses/${businessId}/search-console/properties`, {
      method: 'POST',
      body: { siteUrl },
    }),
  syncSearchConsole: (businessId: number | string) =>
    request<SyncResultSummary>(`/businesses/${businessId}/search-console/sync`, { method: 'POST' }),
  getSearchConsoleMetrics: (businessId: number | string) =>
    request<SearchConsoleSummaryDto>(`/businesses/${businessId}/search-console/metrics`),

  // Google Business Profile
  getGoogleBusinessLocations: (businessId: number | string) =>
    request<{
      connectedLocation: GoogleBusinessConnectionDto | null;
      availableLocations: GoogleBusinessLocationOption[];
      locations?: GoogleBusinessLocationOption[];
      selectedLocation?: GoogleBusinessConnectionDto | null;
      error?: string | null;
    }>(
      `/businesses/${businessId}/google-business/locations`
    ),
  selectGoogleBusinessLocation: (
    businessId: number | string,
    data: { locationId: string; locationName: string; accountId?: string }
  ) =>
    request<GoogleBusinessConnectionDto>(`/businesses/${businessId}/google-business/locations`, {
      method: 'POST',
      body: data,
    }),
  syncGoogleBusiness: (businessId: number | string) =>
    request<SyncResultSummary>(`/businesses/${businessId}/google-business/sync`, { method: 'POST' }),
  getGoogleBusinessMetrics: (businessId: number | string) =>
    request<GoogleBusinessSummaryDto>(`/businesses/${businessId}/google-business/metrics`),
};

// -------------------------
// Types
// -------------------------
export interface AuthUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  isSuperAdmin?: boolean;
}

export interface OrgSummary {
  id: number;
  name: string;
  slug: string;
}

export interface MeResponse extends AuthUser {
  status: string;
  isSuperAdmin?: boolean;
  moduleAccess?: any;
  memberships: Array<{
    organizationId: number;
    organizationName: string;
    organizationSlug: string;
    role: string;
    joinedAt: string;
  }>;
}

export interface BusinessLocation {
  id: number;
  businessId: number;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  isActive: boolean;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessService {
  id: number;
  businessId: number;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessTargetLocation {
  id: number;
  businessId: number;
  name: string;
  city: string | null;
  state: string | null;
  country: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessOnboarding {
  id: number;
  businessId: number;
  currentStep: number;
  completed: boolean;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessSettings {
  id: number;
  businessId: number;
  primaryIndustry: string | null;
  primaryCategory: string | null;
  secondaryCategories: string[] | null;
  targetAudience: string | null;
  targetMarket: string | null;
  createdAt: string;
  updatedAt: string;
}

export type SEOProjectStatus = 'NOT_STARTED' | 'ANALYZING' | 'ACTIVE' | 'ERROR';

export interface SEOProject {
  id: number;
  businessId: number;
  status: SEOProjectStatus;
  seoScore: number | null;
  lastAnalyzedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// Phase 3 Website Audit Types
export type WebsiteAuditStatus = 'QUEUED' | 'CRAWLING' | 'ANALYZING' | 'COMPLETED' | 'FAILED';
export type SEOFindingCategory = 'TECHNICAL' | 'ON_PAGE' | 'LOCAL' | 'CONTENT' | 'INDEXABILITY';
export type SEOFindingSeverity = 'INFO' | 'WARNING' | 'ERROR';

export interface SEOFinding {
  id: number;
  auditId: number;
  pageId: number | null;
  category: SEOFindingCategory;
  severity: SEOFindingSeverity;
  code: string;
  title: string;
  description: string;
  recommendation: string;
  createdAt: string;
  page?: {
    id: number;
    url: string;
    title: string | null;
  };
}

export interface WebsitePage {
  id: number;
  auditId: number;
  url: string;
  depth: number;
  statusCode: number;
  contentType: string | null;
  responseTime: number | null;
  title: string | null;
  metaDescription: string | null;
  canonicalUrl: string | null;
  robotsMeta: string | null;
  h1Count: number;
  h2Count: number;
  wordCount: number;
  imageCount: number;
  missingAltCount: number;
  internalLinkCount: number;
  externalLinkCount: number;
  hasStructuredData: boolean;
  hasOpenGraph: boolean;
  hasViewport: boolean;
  language: string | null;
  createdAt: string;
  findings?: SEOFinding[];
}

export interface WebsiteAudit {
  id: number;
  businessId: number;
  auditNumber?: number;
  status: WebsiteAuditStatus;
  startedAt: string | null;
  completedAt: string | null;
  pagesCrawled: number;
  pagesFailed: number;
  overallScore: number | null;
  technicalScore: number | null;
  onPageScore: number | null;
  localScore: number | null;
  contentScore: number | null;
  indexabilityScore: number | null;
  createdAt: string;
  updatedAt: string;
  findings?: SEOFinding[];
  pages?: WebsitePage[];
  _count?: {
    pages: number;
    findings: number;
  };
}

export interface Business {
  id: number;
  organizationId: number;
  name: string;
  website: string | null;
  industry: string | null;
  description: string | null;
  phone: string | null;
  email: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  locations: BusinessLocation[];
  services?: BusinessService[];
  targetLocations?: BusinessTargetLocation[];
  onboarding?: BusinessOnboarding | null;
  settings?: BusinessSettings | null;
  seoProject?: SEOProject | null;
  websiteAudits?: WebsiteAudit[];
}

export interface CreateBusinessBody {
  name: string;
  website?: string;
  industry?: string;
  description?: string;
  phone?: string;
  email?: string;
  location?: {
    name?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
    latitude?: number;
    longitude?: number;
    phone?: string;
  };
  services?: Array<{ name: string; description?: string }>;
  targetLocations?: Array<{ name: string; city?: string; state?: string; country?: string }>;
  settings?: {
    primaryIndustry?: string;
    primaryCategory?: string;
    secondaryCategories?: string[];
    targetAudience?: string;
    targetMarket?: string;
  };
}

// -------------------------
// Phase 4 Google & Search Console Types
// -------------------------
export type GoogleConnectionStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'ERROR';
export type GoogleBusinessConnectionStatus = 'CONNECTED' | 'DISCONNECTED' | 'ERROR';
export type SearchConsolePropertyType = 'URL_PREFIX' | 'DOMAIN';
export type GoogleBusinessMetricType =
  | 'SEARCH_IMPRESSIONS'
  | 'MAPS_IMPRESSIONS'
  | 'CALL_CLICKS'
  | 'WEBSITE_CLICKS'
  | 'DIRECTION_REQUESTS'
  | 'BUSINESS_CONVERSATIONS';

export interface GoogleConnectionDto {
  id: number;
  organizationId: number;
  googleAccountEmail: string | null;
  scopes: string;
  status: GoogleConnectionStatus;
  accessTokenExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GoogleStatusResponse {
  configured: boolean;
  connected: boolean;
  connection: GoogleConnectionDto | null;
}

export interface SearchConsolePropertyDto {
  id: number;
  businessId: number;
  googleConnectionId: number;
  siteUrl: string;
  propertyType: SearchConsolePropertyType;
  verified: boolean;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SearchConsolePropertyOption {
  siteUrl: string;
  permissionLevel: string;
}

export interface SearchConsoleMetricDto {
  id: number;
  businessId: number;
  propertyId: number;
  date: string;
  query: string | null;
  page: string | null;
  country: string | null;
  device: string | null;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  createdAt: string;
}

export interface SearchConsoleSummaryDto {
  totalClicks: number;
  totalImpressions: number;
  averageCtr: number;
  averagePosition: number;
  dateRange: {
    start: string;
    end: string;
  };
  topQueries: Array<{
    query: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>;
  topPages: Array<{
    page: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>;
}

export interface GoogleBusinessConnectionDto {
  id: number;
  businessId: number;
  googleConnectionId: number;
  googleAccountId: string | null;
  googleLocationId: string | null;
  googleLocationName: string | null;
  status: GoogleBusinessConnectionStatus;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GoogleBusinessLocationOption {
  accountId: string;
  accountName: string;
  locationId: string;
  locationName: string;
  address?: string;
}

export interface GoogleBusinessMetricDto {
  id: number;
  businessId: number;
  googleBusinessConnectionId: number;
  date: string;
  metricType: GoogleBusinessMetricType;
  value: number;
  createdAt: string;
}

export interface GoogleBusinessSearchKeywordDto {
  id: number;
  businessId: number;
  googleBusinessConnectionId: number;
  month: string;
  keyword: string;
  impressions: number;
  createdAt: string;
  updatedAt: string;
}

export interface GoogleBusinessSummaryDto {
  searchImpressions: number;
  mapsImpressions: number;
  totalImpressions: number;
  callClicks: number;
  websiteClicks: number;
  directionRequests: number;
  conversations: number;
  dateRange: {
    start: string;
    end: string;
  };
  topKeywords: Array<{
    keyword: string;
    impressions: number;
  }>;
}

export interface SyncResultSummary {
  success: boolean;
  message: string;
  metricsCount: number;
  keywordsCount?: number;
  period: string;
  syncedAt: string;
}

// -------------------------
// Phase 5A: SEO Opportunity & Intelligence API
// -------------------------

export type OpportunityCategory =
  | 'TECHNICAL'
  | 'ON_PAGE'
  | 'LOCAL_SEO'
  | 'CONTENT'
  | 'SEARCH_CONSOLE'
  | 'GOOGLE_BUSINESS_PROFILE';

export type OpportunityPriority = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export type OpportunityStatus = 'OPEN' | 'IN_PROGRESS' | 'COMPLETED' | 'DISMISSED';

export interface SEOOpportunityDto {
  id: number;
  businessId: number;
  type: string;
  category: OpportunityCategory;
  priority: OpportunityPriority;
  status: OpportunityStatus;
  title: string;
  description: string;
  recommendation: string;
  impactScore: number;
  confidenceScore: number;
  evidenceJson: Record<string, any>;
  targetUrl: string | null;
  targetKeyword: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface OpportunityEngineSummaryDto {
  totalOpportunities: number;
  openOpportunities: number;
  openCount?: number;
  inProgressCount?: number;
  completedCount?: number;
  dismissedCount?: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  opportunityScore: number;
  generatedCount: number;
}

export interface OpportunityQueryFilters {
  category?: OpportunityCategory;
  priority?: OpportunityPriority;
  status?: OpportunityStatus;
}

export const intelligenceApi = {
  generate: (businessId: number) =>
    request<{ generatedCount: number; summary: OpportunityEngineSummaryDto }>(
      `/businesses/${businessId}/intelligence/generate`,
      { method: 'POST' }
    ),

  getSummary: (businessId: number) =>
    request<OpportunityEngineSummaryDto>(`/businesses/${businessId}/intelligence/summary`),

  getOpportunities: (businessId: number, filters?: OpportunityQueryFilters) => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.status) params.append('status', filters.status);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request<SEOOpportunityDto[]>(
      `/businesses/${businessId}/intelligence/opportunities${qs}`
    );
  },

  getOpportunityById: (businessId: number, id: number) =>
    request<SEOOpportunityDto>(`/businesses/${businessId}/intelligence/opportunities/${id}`),

  updateStatus: (businessId: number, id: number, status: OpportunityStatus) =>
    request<SEOOpportunityDto>(`/businesses/${businessId}/intelligence/opportunities/${id}`, {
      method: 'PATCH',
      body: { status },
    }),
};

// -------------------------
// Phase 5B: AI SEO Analyst API
// -------------------------

export interface AIOpportunityAnalysisResult {
  summary: string;
  whyItMatters: string;
  recommendedActions: string[];
  risks: string[];
  confidenceNote: string;
  suggestedNextStep: string;
}

export interface AITitleSuggestion {
  title: string;
  reason: string;
}

export interface AITitleSuggestionsResult {
  suggestions: AITitleSuggestion[];
}

export interface AIMetaSuggestion {
  metaDescription: string;
  reason: string;
}

export interface AIMetaSuggestionsResult {
  suggestions: AIMetaSuggestion[];
}

export interface AIContentSection {
  heading: string;
  keyPoints: string[];
}

export interface AIFaqIdea {
  question: string;
  answerGuidance: string;
}

export interface AIContentBriefResult {
  suggestedPageTopic: string;
  searchIntent: string;
  primaryTopic: string;
  relatedTopics: string[];
  suggestedH1: string;
  sectionOutline: AIContentSection[];
  localInformationToInclude: string[];
  internalLinkingSuggestions: string[];
  faqIdeas: AIFaqIdea[];
}

export interface AIWeeklySummaryResult {
  performanceSummary: string;
  importantChanges: string[];
  topOpportunities: string[];
  completedOpportunities: string[];
  recommendedPriorities: string[];
}

export interface AIAnalysisRecordDto {
  id: number;
  businessId: number;
  opportunityId: number | null;
  analysisType: string;
  provider: string;
  model: string;
  resultJson: any;
  createdAt: string;
  updatedAt: string;
}

export const FRONTEND_AI_TIMEOUT_MS = 120000;

export const aiApi = {
  analyzeOpportunity: (businessId: number, opportunityId: number, force = false) =>
    request<{ analysis: AIOpportunityAnalysisResult; cached: boolean; record: AIAnalysisRecordDto }>(
      `/businesses/${businessId}/intelligence/opportunities/${opportunityId}/analyze${force ? '?force=true' : ''}`,
      { method: 'POST', timeoutMs: FRONTEND_AI_TIMEOUT_MS }
    ),

  getLatestAnalysis: (businessId: number, opportunityId: number) =>
    request<AIAnalysisRecordDto | null>(
      `/businesses/${businessId}/intelligence/opportunities/${opportunityId}/analysis`
    ),

  generateTitleSuggestions: (businessId: number, opportunityId: number, force = false) =>
    request<{ suggestions: AITitleSuggestionsResult; cached: boolean; record: AIAnalysisRecordDto }>(
      `/businesses/${businessId}/intelligence/opportunities/${opportunityId}/title-suggestions${force ? '?force=true' : ''}`,
      { method: 'POST', timeoutMs: FRONTEND_AI_TIMEOUT_MS }
    ),

  generateMetaSuggestions: (businessId: number, opportunityId: number, force = false) =>
    request<{ suggestions: AIMetaSuggestionsResult; cached: boolean; record: AIAnalysisRecordDto }>(
      `/businesses/${businessId}/intelligence/opportunities/${opportunityId}/meta-suggestions${force ? '?force=true' : ''}`,
      { method: 'POST', timeoutMs: FRONTEND_AI_TIMEOUT_MS }
    ),

  generateContentBrief: (businessId: number, opportunityId: number, force = false) =>
    request<{ brief: AIContentBriefResult; cached: boolean; record: AIAnalysisRecordDto }>(
      `/businesses/${businessId}/intelligence/opportunities/${opportunityId}/content-brief${force ? '?force=true' : ''}`,
      { method: 'POST', timeoutMs: FRONTEND_AI_TIMEOUT_MS }
    ),

  generateWeeklySummary: (businessId: number, force = false) =>
    request<{ summary: AIWeeklySummaryResult; cached: boolean; record: AIAnalysisRecordDto }>(
      `/businesses/${businessId}/intelligence/weekly-summary${force ? '?force=true' : ''}`,
      { method: 'POST', timeoutMs: FRONTEND_AI_TIMEOUT_MS }
    ),
};

// -------------------------
// Phase 6 & 7: Automation & Operations API
// -------------------------

export interface AutomationSummaryDto {
  total: number;
  running: number;
  queued: number;
  completed: number;
  failed: number;
  cancelled: number;
  completedToday: number;
  failedToday: number;
  staleRunning: number;
}

export interface AutomationExecutionDto {
  id: string;
  organizationId: number;
  businessId: number;
  workflowKey: string;
  triggerType: string;
  status: string;
  n8nExecutionId: string | null;
  errorMessage: string | null;
  inputJson: Record<string, unknown> | null;
  outputJson: Record<string, unknown> | null;
  createdAt: string;
  completedAt: string | null;
}

export interface AutomationExecutionListResponse {
  items: AutomationExecutionDto[];
  total: number;
  limit: number;
  offset: number;
}

export const automationApi = {
  getSummary: (businessId?: number | string) => {
    const q = businessId ? `?businessId=${businessId}` : '';
    return request<AutomationSummaryDto>(`/automation/summary${q}`);
  },

  listExecutions: (params?: {
    businessId?: number | string;
    workflowKey?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.businessId) q.append('businessId', String(params.businessId));
    if (params?.workflowKey) q.append('workflowKey', params.workflowKey);
    if (params?.status) q.append('status', params.status);
    if (params?.limit !== undefined) q.append('limit', String(params.limit));
    if (params?.offset !== undefined) q.append('offset', String(params.offset));
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<AutomationExecutionListResponse>(`/automation/executions${qs}`);
  },
};

// -------------------------
// Phase 7+: Keywords Module API
// -------------------------

export type KeywordPositionBucket = 'all' | 'top3' | '4-10' | '11-20' | '21-50' | '51+';
export type KeywordSortField = 'clicks' | 'impressions' | 'ctr' | 'position' | 'query';
export type SortOrder = 'asc' | 'desc';
export type KeywordDateRange = '7d' | '28d' | '3m' | '6m' | 'all';

export type KeywordOpportunityType =
  | 'PAGE_2_OPPORTUNITY'
  | 'HIGH_IMPRESSIONS_LOW_CTR'
  | 'HIGH_CTR_LOW_POS'
  | 'HIGH_CLICKS'
  | 'TOP_10_OPPORTUNITY';

export interface KeywordOpportunityDto {
  query: string;
  type: KeywordOpportunityType;
  label: string;
  position: number;
  impressions: number;
  clicks: number;
  ctr: number;
  description: string;
  recommendedAction: string;
}

export interface KeywordItemDto {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  pages: string[];
  opportunity?: KeywordOpportunityDto;
}

export interface KeywordsSummaryDto {
  totalKeywords: number;
  top10Keywords: number;
  averagePosition: number;
  totalImpressions: number;
  totalClicks: number;
  averageCtr: number;
  dateRange: {
    start: string;
    end: string;
  };
}

export interface KeywordsResponseDto {
  connected: boolean;
  property: {
    siteUrl: string;
    propertyType: SearchConsolePropertyType;
    verified: boolean;
    lastSyncedAt: string | null;
  } | null;
  summary: KeywordsSummaryDto;
  keywords: KeywordItemDto[];
  opportunities: KeywordOpportunityDto[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface KeywordDailyMetricDto {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface KeywordPageMetricDto {
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface KeywordDeviceMetricDto {
  device: string;
  clicks: number;
  impressions: number;
}

export interface KeywordCountryMetricDto {
  country: string;
  clicks: number;
  impressions: number;
}

export interface KeywordDetailResponseDto {
  query: string;
  totalClicks: number;
  totalImpressions: number;
  averageCtr: number;
  averagePosition: number;
  opportunity?: KeywordOpportunityDto;
  dailyMetrics: KeywordDailyMetricDto[];
  topPages: KeywordPageMetricDto[];
  devices: KeywordDeviceMetricDto[];
  countries: KeywordCountryMetricDto[];
}

export const keywordsApi = {
  getKeywords: (
    businessId: number,
    params?: {
      dateRange?: KeywordDateRange;
      startDate?: string;
      endDate?: string;
      search?: string;
      positionBucket?: KeywordPositionBucket;
      sortBy?: KeywordSortField;
      sortOrder?: SortOrder;
      page?: number;
      pageSize?: number;
    }
  ) => {
    const q = new URLSearchParams();
    if (params?.dateRange) q.append('dateRange', params.dateRange);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    if (params?.search) q.append('search', params.search);
    if (params?.positionBucket) q.append('positionBucket', params.positionBucket);
    if (params?.sortBy) q.append('sortBy', params.sortBy);
    if (params?.sortOrder) q.append('sortOrder', params.sortOrder);
    if (params?.page) q.append('page', String(params.page));
    if (params?.pageSize) q.append('pageSize', String(params.pageSize));
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<KeywordsResponseDto>(`/businesses/${businessId}/keywords${qs}`);
  },

  getKeywordDetail: (businessId: number, query: string) => {
    const q = new URLSearchParams({ query });
    return request<KeywordDetailResponseDto>(
      `/businesses/${businessId}/keywords/detail?${q.toString()}`
    );
  },

  sync: (businessId: number) => {
    return request<SyncResultSummary>(`/businesses/${businessId}/search-console/sync`, {
      method: 'POST',
    });
  },
};

// -------------------------
// Phase 7+ Step 2: Reviews API
// -------------------------

export interface ReviewerDto {
  displayName: string;
  profilePhotoUrl?: string;
  isAnonymous?: boolean;
}

export interface ReviewReplyDto {
  comment: string;
  updateTime: string;
}

export interface ReviewItemDto {
  reviewId: string;
  name: string;
  reviewer: ReviewerDto;
  starRating: number;
  comment: string;
  createTime: string;
  updateTime: string;
  reviewReply?: ReviewReplyDto | null;
  locationName?: string;
}

export interface ReviewDistributionDto {
  fiveStar: number;
  fourStar: number;
  threeStar: number;
  twoStar: number;
  oneStar: number;
}

export interface ReviewsSummaryDto {
  totalReviews: number;
  averageRating: number;
  unansweredReviews: number;
  responseRate: number;
  ratingDistribution: ReviewDistributionDto;
}

export interface ReviewsResponseDto {
  summary: ReviewsSummaryDto;
  reviews: ReviewItemDto[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    nextPageToken?: string;
  };
  isConnected: boolean;
  hasLocation: boolean;
  locationName?: string | null;
  apiError?: {
    code: string;
    message: string;
    helpUrl?: string;
  } | null;
}

export interface ReviewReplyResponseDto {
  success: boolean;
  comment: string;
  updateTime: string;
}

export interface AISuggestReplyResponseDto {
  suggestion: string;
}

export const reviewsApi = {
  getReviews: (
    businessId: number,
    params?: {
      page?: number;
      pageSize?: number;
      rating?: number | string;
      replyStatus?: 'all' | 'unanswered' | 'replied';
      search?: string;
      dateRange?: string;
    }
  ) => {
    const q = new URLSearchParams();
    if (params?.page) q.append('page', String(params.page));
    if (params?.pageSize) q.append('pageSize', String(params.pageSize));
    if (params?.rating) q.append('rating', String(params.rating));
    if (params?.replyStatus) q.append('replyStatus', params.replyStatus);
    if (params?.search) q.append('search', params.search);
    if (params?.dateRange) q.append('dateRange', params.dateRange);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<ReviewsResponseDto>(`/businesses/${businessId}/reviews${qs}`);
  },

  replyToReview: (businessId: number, reviewId: string, comment: string) => {
    return request<ReviewReplyResponseDto>(
      `/businesses/${businessId}/reviews/${encodeURIComponent(reviewId)}/reply`,
      {
        method: 'POST',
        body: JSON.stringify({ comment }),
      }
    );
  },

  suggestAiReply: (
    businessId: number,
    data: { reviewText: string; starRating: number; reviewerName?: string }
  ) => {
    return request<AISuggestReplyResponseDto>(
      `/businesses/${businessId}/reviews/suggest-reply`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  sync: (businessId: number) => {
    return request<SyncResultSummary>(`/businesses/${businessId}/google-business/sync`, {
      method: 'POST',
    });
  },
};

// -------------------------
// Competitors Module — Phase 7+ Step 3
// -------------------------

export type CompetitorStatus = 'ACTIVE' | 'PAUSED' | 'ARCHIVED';

export interface CompetitorAnalysisDto {
  url: string;
  pageTitle: string | null;
  metaDescription: string | null;
  h1: string | null;
  canonicalUrl: string | null;
  isHttps: boolean;
  pageCount: number;
  technicalIssues: string[];
  analyzedAt: string;
  overallScore?: number | null;
}

export interface CompetitorDto {
  id: number;
  businessId: number;
  name: string;
  websiteUrl: string;
  domain: string;
  googleMapsUrl: string | null;
  location: string | null;
  notes: string | null;
  status: CompetitorStatus;
  lastAnalyzedAt: string | null;
  analysis: CompetitorAnalysisDto | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompetitorOpportunityDto {
  type: string;
  title: string;
  description: string;
  impact: 'HIGH' | 'MEDIUM' | 'LOW';
  recommendation: string;
}

export interface CompetitorComparisonDto {
  business: {
    name: string;
    websiteUrl: string | null;
    domain: string | null;
    isHttps: boolean | null;
    pageTitle: string | null;
    metaDescription: string | null;
    h1: string | null;
    pageCount: number | null;
    technicalIssuesCount: number | null;
    technicalIssues: string[];
    lastAuditDate: string | null;
    overallScore: number | null;
  };
  competitor: {
    id: number;
    name: string;
    websiteUrl: string;
    domain: string;
    location: string | null;
    googleMapsUrl: string | null;
    isHttps: boolean | null;
    pageTitle: string | null;
    metaDescription: string | null;
    h1: string | null;
    pageCount: number | null;
    technicalIssuesCount: number | null;
    technicalIssues: string[];
    lastAnalyzedAt: string | null;
    overallScore: number | null;
  };
  signals: {
    gbpDataAvailable: false;
    gbpUnavailableReason: string;
    privateKeywordsAvailable: false;
    privateKeywordsUnavailableReason: string;
  };
  opportunities: CompetitorOpportunityDto[];
}

export interface CreateCompetitorRequest {
  name: string;
  websiteUrl: string;
  googleMapsUrl?: string | null;
  location?: string | null;
  notes?: string | null;
}

export interface UpdateCompetitorRequest {
  name?: string;
  websiteUrl?: string;
  googleMapsUrl?: string | null;
  location?: string | null;
  notes?: string | null;
  status?: CompetitorStatus;
}

export interface CompetitorListResponseDto {
  items: CompetitorDto[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

export const competitorsApi = {
  list: (
    businessId: number | string,
    params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: CompetitorStatus;
    }
  ) => {
    const q = new URLSearchParams();
    if (params?.page) q.append('page', String(params.page));
    if (params?.pageSize) q.append('pageSize', String(params.pageSize));
    if (params?.search) q.append('search', params.search);
    if (params?.status) q.append('status', params.status);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<CompetitorListResponseDto>(`/businesses/${businessId}/competitors${qs}`);
  },

  get: (businessId: number | string, competitorId: number | string) =>
    request<CompetitorDto>(`/businesses/${businessId}/competitors/${competitorId}`),

  create: (businessId: number | string, body: CreateCompetitorRequest) =>
    request<CompetitorDto>(`/businesses/${businessId}/competitors`, {
      method: 'POST',
      body,
    }),

  update: (
    businessId: number | string,
    competitorId: number | string,
    body: UpdateCompetitorRequest
  ) =>
    request<CompetitorDto>(`/businesses/${businessId}/competitors/${competitorId}`, {
      method: 'PATCH',
      body,
    }),

  delete: (businessId: number | string, competitorId: number | string) =>
    request<{ success: boolean; message: string }>(
      `/businesses/${businessId}/competitors/${competitorId}`,
      { method: 'DELETE' }
    ),

  analyze: (businessId: number | string, competitorId: number | string) =>
    request<CompetitorDto>(`/businesses/${businessId}/competitors/${competitorId}/analyze`, {
      method: 'POST',
    }),

  compare: (businessId: number | string, competitorId: number | string) =>
    request<CompetitorComparisonDto>(
      `/businesses/${businessId}/competitors/${competitorId}/compare`
    ),
};

// =============================================================
// Reports Module — Phase 7+ Step 4
// =============================================================

export type {
  ReportType,
  ReportPeriod,
  ReportMeta,
  ReportCardDto,
  ReportListResponseDto,
  SeoOverviewReportDto,
  WebsiteAuditReportDto,
  SearchPerformanceReportDto,
  GbpPerformanceReportDto,
  SeoOpportunitiesReportDto,
  AiInsightsReportDto,
  CompetitorComparisonReportDto,
  ReportExportRequest,
} from '@local-seo-ai/shared';

import type {
  ReportType,
  ReportPeriod,
  ReportListResponseDto,
  SeoOverviewReportDto,
  WebsiteAuditReportDto,
  SearchPerformanceReportDto,
  GbpPerformanceReportDto,
  SeoOpportunitiesReportDto,
  AiInsightsReportDto,
  CompetitorComparisonReportDto,
} from '@local-seo-ai/shared';

export interface ReportQueryParams {
  period?: ReportPeriod;
  startDate?: string;
  endDate?: string;
}

export const reportsApi = {
  list: (businessId: number | string) =>
    request<ReportListResponseDto>(`/businesses/${businessId}/reports`),

  getSeoOverview: (businessId: number | string, params?: ReportQueryParams) => {
    const q = new URLSearchParams();
    if (params?.period) q.append('period', params.period);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<SeoOverviewReportDto>(`/businesses/${businessId}/reports/seo_overview${qs}`);
  },

  getWebsiteAudit: (businessId: number | string, params?: ReportQueryParams) => {
    const q = new URLSearchParams();
    if (params?.period) q.append('period', params.period);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<WebsiteAuditReportDto>(`/businesses/${businessId}/reports/website_audit${qs}`);
  },

  getSearchPerformance: (businessId: number | string, params?: ReportQueryParams) => {
    const q = new URLSearchParams();
    if (params?.period) q.append('period', params.period);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<SearchPerformanceReportDto>(`/businesses/${businessId}/reports/search_performance${qs}`);
  },

  getGbpPerformance: (businessId: number | string, params?: ReportQueryParams) => {
    const q = new URLSearchParams();
    if (params?.period) q.append('period', params.period);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<GbpPerformanceReportDto>(`/businesses/${businessId}/reports/gbp_performance${qs}`);
  },

  getSeoOpportunities: (businessId: number | string, params?: ReportQueryParams) => {
    const q = new URLSearchParams();
    if (params?.period) q.append('period', params.period);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<SeoOpportunitiesReportDto>(`/businesses/${businessId}/reports/seo_opportunities${qs}`);
  },

  getAiInsights: (businessId: number | string, params?: ReportQueryParams) => {
    const q = new URLSearchParams();
    if (params?.period) q.append('period', params.period);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<AiInsightsReportDto>(`/businesses/${businessId}/reports/ai_insights${qs}`);
  },

  getCompetitorComparison: (businessId: number | string, params?: ReportQueryParams) => {
    const q = new URLSearchParams();
    if (params?.period) q.append('period', params.period);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<CompetitorComparisonReportDto>(`/businesses/${businessId}/reports/competitor_comparison${qs}`);
  },

  getReport: (
    businessId: number | string,
    reportType: ReportType,
    params?: ReportQueryParams
  ) => {
    const q = new URLSearchParams();
    if (params?.period) q.append('period', params.period);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return request<any>(`/businesses/${businessId}/reports/${reportType}${qs}`);
  },

  downloadExport: async (
    businessId: number | string,
    reportType: ReportType,
    format: 'csv' | 'json',
    params?: ReportQueryParams
  ) => {
    const q = new URLSearchParams();
    q.append('format', format);
    if (params?.period) q.append('period', params.period);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    const qs = q.toString() ? `?${q.toString()}` : '';

    const storedToken = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {};
    if (storedToken) {
      headers['Authorization'] = `Bearer ${storedToken}`;
    }

    const response = await fetch(`${API_BASE}/businesses/${businessId}/reports/${reportType}/export${qs}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Export failed with HTTP status ${response.status}`);
    }

    if (format === 'json') {
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_${params?.period ?? '30d'}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } else {
      const text = await response.text();
      const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_${params?.period ?? '30d'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }
  },
};




// -------------------------
// Admin
// -------------------------
export interface AIPoolItem {
  id: string;
  maskedKey: string;
  label: string;
  isActive: boolean;
  status: 'ACTIVE' | 'RATE_LIMITED' | 'ERROR';
  cooldownSecondsRemaining?: number;
  lastUsedAt?: string | null;
}

export interface AIConfigData {
  provider: 'gemini' | 'openai' | 'mock';
  geminiModel: string;
  openaiModel: string;
  geminiPool: AIPoolItem[];
  openaiPool: AIPoolItem[];
  hasGeminiKey?: boolean;
  geminiKeyMasked?: string | null;
  hasOpenaiKey?: boolean;
  openaiKeyMasked?: string | null;
  timeoutMs: number;
  lastUpdated?: string | null;
}

export interface UpdateAIConfigPayload {
  provider?: 'gemini' | 'openai' | 'mock';
  geminiModel?: string;
  openaiModel?: string;
  geminiKey?: string;
  openaiKey?: string;
  timeoutMs?: number;
  geminiPool?: Array<{ id?: string; key?: string; label?: string; isActive?: boolean }>;
  openaiPool?: Array<{ id?: string; key?: string; label?: string; isActive?: boolean }>;
}

export interface TestAIConfigPayload {
  provider: 'gemini' | 'openai';
  model?: string;
  keyId?: string;
  apiKey?: string;
}

export interface TestAIConfigResult {
  success: boolean;
  latencyMs: number;
  message: string;
  sampleOutput?: string;
}

export const adminApi = {
  getUsers: () => request<any[]>('/admin/users'),
  createUser: (body: any) => request<any>('/admin/users', { method: 'POST', body }),
  updateUser: (id: number, body: any) => request<any>(`/admin/users/${id}`, { method: 'PUT', body }),
  deleteUser: (id: number) => request<any>(`/admin/users/${id}`, { method: 'DELETE' }),
  getAIConfig: () => request<AIConfigData>('/admin/ai-config'),
  saveAIConfig: (body: UpdateAIConfigPayload) => request<AIConfigData>('/admin/ai-config', { method: 'PUT', body }),
  testAIConfig: (body: TestAIConfigPayload) => request<TestAIConfigResult>('/admin/ai-config/test', { method: 'POST', body }),
  setActiveKeyInPool: (provider: 'gemini' | 'openai', keyId: string) =>
    request<AIConfigData>('/admin/ai-config/pool/set-active', { method: 'POST', body: { provider, keyId } }),
  addKeyToPool: (provider: 'gemini' | 'openai', key: string, label?: string) =>
    request<AIConfigData>('/admin/ai-config/pool/add', { method: 'POST', body: { provider, key, label } }),
  removeKeyFromPool: (provider: 'gemini' | 'openai', keyId: string) =>
    request<AIConfigData>(`/admin/ai-config/pool/${provider}/${keyId}`, { method: 'DELETE' }),
};

